import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Star, ArrowRight, Loader2 } from 'lucide-react';
import { PricingFeatureList } from './PricingFeatureList';
import { PricingPlan } from '@/data/pricingPlans';

interface PlanCardProps {
  plan: PricingPlan;
  variant?: 'public' | 'admin';
  index?: number;
  isCurrentPlan?: boolean;
  isLoading?: boolean;
  onSelectPlan?: (plan: PricingPlan) => void;
  className?: string;
  // Admin-specific props
  isRecommended?: boolean;
  customFeatures?: string[];
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  variant = 'public',
  index = 0,
  isCurrentPlan = false,
  isLoading = false,
  onSelectPlan,
  className = '',
  isRecommended,
  customFeatures
}) => {
  const showPopular = plan.popular || isRecommended;
  const features = customFeatures || plan.features;

  const handleSelect = () => {
    if (onSelectPlan && !isCurrentPlan && !isLoading) {
      onSelectPlan(plan);
    }
  };

  const cardContent = (
    <div className="relative h-full flex flex-col">
      {showPopular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
          <Badge className="bg-[#FA8C00] text-white px-4 py-1 flex items-center gap-1">
            <Star className="h-3 w-3" />
            {variant === 'admin' ? 'Recommended' : 'Most Popular'}
          </Badge>
        </div>
      )}
      <Card className={`flex flex-col h-full transition-all duration-300 hover:shadow-xl hover:scale-[1.02] ${
        showPopular ? 'border-[#FA8C00] border-2' : 'border-gray-200'
      } ${isCurrentPlan ? 'bg-gray-50' : ''} ${className}`}>
        <CardHeader className="text-center pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-bold text-gray-800 text-center w-full">
              {plan.name}
            </CardTitle>
            {isCurrentPlan && (
              <span className="text-xs font-medium px-2 py-1 bg-primary/10 text-primary rounded absolute top-2 right-2">
                Current Plan
              </span>
            )}
          </div>
          {plan.description && (
            <p className="text-sm text-gray-600 mt-2">{plan.description}</p>
          )}
          <div className="mt-4">
            <span className="text-4xl font-bold text-gray-800">
              ${plan.price}
            </span>
            <span className="text-lg text-gray-600">/{plan.period}</span>
          </div>
          {plan.trialDays > 0 && plan.id === 'essential' && (
            <p className="text-sm text-[#FA8C00] font-medium mt-2">
              {plan.trialDays}-day free trial
            </p>
          )}
        </CardHeader>
        {/* Make CardContent fill available space and align button at bottom */}
        <CardContent className="pt-0 flex-1 flex flex-col">
          {/* Features */}
          {features && features.length > 0 && (
            <div className="text-left mb-4">
              <PricingFeatureList features={features} variant="simple" />
            </div>
          )}
          {/* Spacer to push button to bottom */}
          <div className="flex-1" />
          <div className="pt-6">
            <Button
              onClick={handleSelect}
              variant={plan.buttonVariant}
              disabled={isLoading || isCurrentPlan}
              className={`w-full transform transition-all duration-300 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] rounded-xl ${
                showPopular
                  ? 'bg-gradient-to-r from-[#FA8C00] to-[#FA8C00]/90 hover:from-[#FA8C00]/90 hover:to-[#FA8C00]/80 text-white'
                  : plan.buttonVariant === 'default'
                  ? 'bg-gray-900 hover:bg-gray-800 text-white'
                  : 'border-2 border-[#FA8C00] text-[#FA8C00] hover:bg-[#FA8C00]/10 hover:border-[#FA8C00]/80'
              } ${isCurrentPlan ? 'bg-gray-200 text-gray-500 hover:bg-gray-200' : ''}`}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : isCurrentPlan ? (
                'Your Current Plan'
              ) : (
                <>
                  {variant === 'admin' && plan.id !== 'enterprise' ? 'Upgrade' : plan.buttonText}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // For public variant, wrap with motion
  if (variant === 'public') {
    return (
      <motion.div
        variants={itemVariants}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.6, delay: index * 0.1 }}
        className="relative h-full flex flex-col"
      >
        {cardContent}
      </motion.div>
    );
  }
  // For admin variant, return plain div
  return <div className="relative h-full flex flex-col">{cardContent}</div>;
};